/* import __COLOCATED_TEMPLATE__ from './posting-create-feedback.hbs'; */
import EmberObject, { computed } from '@ember/object';
import { readOnly, or } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { ASSIGNMENT_COMMAND_CONSTANTS } from "../../services/commands/assignment-commands";
import { ASSIGNMENT } from "../../models/assignment";
import { showModal } from 'sh-www/utils/modal-controls';
import { EMBER_ROUTE_NAMES } from "../../constants/routes";
import { POSTING_CREATE__ERROR_CODES } from "../../constants/routes/posting-create";
import { task } from 'ember-concurrency';

/**
 * This is the feedback and action component that goes at the bottom of the Posting Create Form.
 */
export default Component.extend({
  /**
   * @inheritDoc
   */
  tagName: 'div',
  classNames: ['SH-c-posting-create-feedback'],
  classNameBindings: [
    '_isSummaryStatusBlock:SH-c-posting-create-feedback--block',
    '_isSummaryStatusGood:SH-c-posting-create-feedback--good',
    '_isSummaryStatusWarn:SH-c-posting-create-feedback--warn'
  ],

  /**
   * Register services
   */
  applicationStateManager: service('state-managers/application'),
  errorTracker: service('error-tracker'),
  state: service('states/routes/posting-create'),
  stateManager: service('state-managers/routes/posting-create'),
  router: service(),
  session: service(),

  currentUser: readOnly('session.currentUser'),

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /*********************************
   * PRIVATE
   **********************************/

  /**
   * @type {Boolean}    True when the form elements should be disabled
   */
  _areFormElementsDisabled: readOnly('state.areFormElementsDisabled'),

  /**
   * @type {Boolean}    True when we want to disable the approve assignments button.
   */
  _isDisableApproveAssignments: computed(
  'state.isApprovePostingRequestInFlight',
  'state.isEditPostingRequestInFlight',
  'state.isTestMatchesRequestInFlight',
  function() {
    return this.get('state.isApprovePostingRequestInFlight') ||
      this.get('state.isEditPostingRequestInFlight') ||
      this.get('state.isTestMatchesRequestInFlight');
  }),

  /**
   * @type {Boolean}    True when we want to disable the 'decline' assignment button.
   */
  _isDisableDeclineAssignment: computed(
  'state.isDeclinePostingRequestInFlight',
  'state.isEditPostingRequestInFlight',
  'state.isTestMatchesRequestInFlight',
  function() {
    return this.get('state.isDeclinePostingRequestInFlight') ||
      this.get('state.isEditPostingRequestInFlight') ||
      this.get('state.isTestMatchesRequestInFlight');
  }),

  /**
   * @type {Boolean}
   */
  _isDisabledCloseSubmissions: computed(
  'state.isEditPostingRequestInFlight',
  function() {
    return this.get('state.isEditPostingRequestInFlight');
  }),

  /**
   * @type {Boolean}
   */
  _isDisabledDeleteDraft: computed(
  'state.isEditPostingRequestInFlight',
  'state.isTestMatchesRequestInFlight',
  function() {
    return this.get('state.isEditPostingRequestInFlight') ||
      this.get('state.isTestMatchesRequestInFlight');
  }),

  /**
   * @type {Boolean}
   */
  _isDisabledPublishPosting: or(
    'state.isTestMatchesRequestInFlight',
    'state.isCreatePostingRequestInFlight'
  ),

  /**
   * @type {Boolean}
   */
  _isDisabledSaveDraft: computed(
  'state.updatedTitle',
  'state.editingPosting',
  'state.isExistingPostingUpdated',
  'state.isEditPostingRequestInFlight',
  'state.isCreatePostingRequestInFlight',
  'state.isTestMatchesRequestInFlight',
  function() {
    if (this.get('state.isCreatePostingRequestInFlight') ||
    this.get('state.isEditPostingRequestInFlight') ||
    this.get('state.isTestMatchesRequestInFlight')) {
      return true;
    }

    let isEnabled = (!this.get('state.editingPosting') && this.get('state.updatedTitle')) ||  // No posting AND we have a title at least.
      (this.get('state.editingPosting') && this.get('state.isExistingPostingUpdated')); // Editing Existing AND it has changed.

    return !isEnabled;
  }),

  /**
   * @type {Boolean}
   */
  _isDisabledSubmitPosting: or(
    'state.isTestMatchesRequestInFlight',
    'state.isCreatePostingRequestInFlight'
  ),

  /**
   * @type {Boolean}
   */
  _isDisabledUpdatePosting: computed(
  'state.isExistingPostingUpdated',
  'state.isEditPostingRequestInFlight',
  'state.isTestMatchesRequestInFlight',
  'state.isNeedsTestMatches',
  'state.isTestMatchesBlocked',
  function() {
    if (this.get('state.isNeedsTestMatches') ||
    this.get('state.isTestMatchesRequestInFlight') ||
    this.get('state.isEditPostingRequestInFlight')) {
      return true;
    }

    return this.get('state.isTestMatchesBlocked') ||
      !this.get('state.isExistingPostingUpdated');
  }),

  /**
   * @type {Boolean}    True if we should show the analyze posting spinner.
   */
  _isShowAnalyzePosting: computed(
  '_isTestMatchesRequestInFlight',
  function() {
    if (this.get('_isTestMatchesRequestInFlight')) {
      return true;
    }

    return false;
  }),

  /**
   * @type {Boolean}      True when we can show the decline posting button
   */
  _isShowAdminDeclineInReviewPostingButton: computed(
  'currentUser.isAdmin',
  'state.editingPosting.status',
  function() {
    return this.get('currentUser.isAdmin') && this.get('state.editingPosting.status') === ASSIGNMENT.STATUS.IN_REVIEW;
  }),

  /**
   * @type {Boolean}    True if approve assignment button should be shown.
   */
  _isShowApproveAssignment: computed(
  'state.canApproveAssignments',
  'state.editingPosting.status',
  function() {
    return this.get('state.canApproveAssignments') &&
      this.get('state.editingPosting.status') === ASSIGNMENT.STATUS.IN_REVIEW;
  }),

  /**
   * @type {Boolean}
   */
  _isShowCloseSubmissions: computed(
  'state.editingPosting',
  'state.editingPosting.status',
  function() {
    return this.get('state.editingPosting') &&
      this.get('state.editingPosting.status') !== ASSIGNMENT.STATUS.DRAFT &&
      this.get('state.editingPosting.status') !== ASSIGNMENT.STATUS.CLOSED;
  }),

  /**
   * @type {Boolean}
   */
  _isShowDeleteDraft: computed(
  'state.editingPosting',
  'state.editingPosting.status',
  function() {
    return this.get('state.editingPosting') && this.get('state.editingPosting.status') === ASSIGNMENT.STATUS.DRAFT;
  }),

  /**
   * @type {Boolean}
   */
  _isShowPublishPosting: computed(
  'currentUser.company.approved',
  'state.editingPosting.status',
  'state.editingPosting', function() {
    return this.get('currentUser.company.approved') &&
      (!this.get('state.editingPosting') || (this.get('state.editingPosting') && this.get('state.editingPosting.status') === ASSIGNMENT.STATUS.DRAFT));
  }),

  /**
   * @type {Boolean}
   */
  _isShowSubmitPosting: computed(
  'currentUser.company.approved',
  'state.editingPosting.status',
  'state.editingPosting', function() {
    return !this.get('currentUser.company.approved') &&
      (!this.get('state.editingPosting') || (this.get('state.editingPosting') && this.get('state.editingPosting.status') === ASSIGNMENT.STATUS.DRAFT));
  }),

  /**
   * @type {Boolean}
   */
  _isShowSaveDraft: computed('state.editingPosting', 'state.editingPosting.status', function() {
    return !this.get('state.editingPosting') || this.get('state.editingPosting.status') === ASSIGNMENT.STATUS.DRAFT;
  }),

  /**
   * @type {Boolean}
   */
  _isShowUpdatePosting: computed(
  'state.editingPosting',
  'state.editingPosting.status',
  function() {
    return this.get('state.editingPosting') &&
      this.get('state.editingPosting.status') !== ASSIGNMENT.STATUS.DRAFT;
  }),

  /**
   * @type {Boolean}    True if the summary status state should be set to BLOCK for this element
   */
  _isSummaryStatusBlock: computed(
  'state.testMatchesSummaryStatus',
  function() {
    return this.get('state.testMatchesSummaryStatus') === ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.BLOCK;
  }),

  /**
   * @type {Boolean}    True if the summary status state should be set to GOOD for this element
   */
  _isSummaryStatusGood: computed(
  'state.testMatchesSummaryStatus',
  'state.updatedPostingType',
  'state.isAllInfoSuppliedForNewPosting',
  function() {
    return this.get('state.testMatchesSummaryStatus') === ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.GOOD;
  }),

  /**
   * @type {Boolean}    True if the summary status state should be set to WARN for this element
   */
  _isSummaryStatusWarn: computed(
  'state.testMatchesSummaryStatus',
  function() {
    return this.get('state.testMatchesSummaryStatus') === ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.WARN;
  }),

  /**
   * @type {Boolean}    True when we have a request in flight to test-matches endpoint
   */
  _isTestMatchesRequestInFlight: readOnly('state.isTestMatchesRequestInFlight'),

  /** @type {String} The identifier for the summary feedback content. */
  _summaryFeedbackID: computed(
    'state.isAllInfoSuppliedForNewPosting',
    'state.isEligibleForSelect',
    'state.testMatchesSummaryText',
    'state.editingPosting.status',
    function () {
      const isAllInfoSupplied = this.get('state.isAllInfoSuppliedForNewPosting');

      if (this.get('state.testMatchesSummaryText')) {
        return 'test-matches-summary';
      }

      if (!isAllInfoSupplied) {
        return 'invalid';
      }

      // If this is a new posting or an existing posting that is not live/closed, show ready text.
      if (
        !this.get('state.editingPosting')
        || [ASSIGNMENT.STATUS.DRAFT, ASSIGNMENT.STATUS.IN_REVIEW].includes(this.get('state.editingPosting.status'))
      ) {
        return 'ready';
      }

      return 'update';
    }
  ),

  /** @type {Ember.Object} The properties of the summary feedback icon, if applicable. */
  _summaryFeedbackIcon: computed(
    '_isSummaryStatusBlock',
    '_isSummaryStatusGood',
    '_isSummaryStatusWarn',
    function () {
      if (this.get('_isSummaryStatusBlock')) {
        return EmberObject.create({ icon: 'block', color: 'lsg-medium-red' });
      } else if (this.get('_isSummaryStatusGood')) {
        return EmberObject.create({ icon: 'check_circle_outline', color: 'lsg-green' });
      } else if (this.get('_isSummaryStatusWarn')) {
        return EmberObject.create({ icon: 'info', color: 'lsg-yellow' });
      } else {
        return EmberObject.create({ icon: '', color: '' });
      }
    }
  ),

  approvePostingTask: task(function *() {
    try {
      yield this.get('stateManager').updateAssignment(true);
    } catch (e) {
      return;
    }

    yield this.get('stateManager').approveAssignment();
  }).drop(),

  actions: {
    /**
     * Called when the admin clicks to approved this pending posting.
     */
    onApprovePostingClicked: function() {
      let modalModel = {
        titleText: 'Are you sure you want to approve?',
        bodyText: 'Once approved, this posting may be automatically blasted to freelancers according to our rules.',
        isShowCancelButton: true,
        actionButtonText: 'Yes, go ahead',
        cancelButtonText: 'No',
        confirmAction: () => {
          this.get('approvePostingTask').perform();
        }
      };

      showModal(this, 'confirmation', modalModel);
    },

    /**
     * Called when user clicks on this particular button
     */
    onCloseSubmissionsClicked: function() {
      this.router.transitionTo(EMBER_ROUTE_NAMES.POSTING__CLOSE, this.state.editingPosting.idWithSlug);
    },

    /**
     *
     */
    onDeclinePostingClicked() {
      this.get('stateManager').declinePosting();
    },

    /**
     * Called when user clicks on this particular button
     */
    onDeleteDraftClicked: function() {
      this.get('stateManager').deleteAssignment().then(() => {
        this.get('applicationStateManager').setNextRoute(EMBER_ROUTE_NAMES.POSTINGS__LIVE);
      });
    },

    /**
     * Called when user clicks on this particular button
     */
    onPublishPostingClicked: function() {
      if ((this.get('state.editingPosting') && this.get('state.editingPosting.status') === ASSIGNMENT.STATUS.DRAFT)) {
        this.get('stateManager').publishDraftAssignment(true);
        return;
      }

      // If eligible for select, don't require test matches.
      const requireTestMatches = !this.get('state.isEligibleForSelect');
      this.get('stateManager').createAssignment(false, requireTestMatches);
    },

    /**
     * Called when user clicks on this particular button
     */
    onSaveDraftClicked: function() {
      this.get('stateManager').saveDraft().catch(() => {
        // TODO: this is shit and the whole stateManager needs refactoring to avoid these crap
        //  antipatterns
        //
        // do nothing here, otherwise the global unhandled exception handler will trigger
        // and log an erroneous error.
      });
    },

    /**
     * Called when user clicks on this particular button
     */
    onSubmitPostingClicked: function() {
      if ((this.get('state.editingPosting') && this.get('state.editingPosting.status') === ASSIGNMENT.STATUS.DRAFT)) {
        this.get('stateManager').publishDraftAssignment(true);
        return;
      }

      // If eligible for select, don't require test matches.
      const requireTestMatches = !this.get('state.isEligibleForSelect');
      this.get('stateManager').createAssignment(false, requireTestMatches);
    },

    /**
     * Called when user clicks on this particular button
     */
    onUpdatePostingClicked: function() {
      this.get('stateManager').updateAssignment(true).catch(e => {
        if (e === POSTING_CREATE__ERROR_CODES.ERROR_NEEDS_TEST_MATCHES_CONFIRM) {
          return;
        }

        this.get('errorTracker').error('onUpdatePostingClicked -- error saving', this, e);
      });
    }
  }
});
